// App
import isNil from 'lodash/isNil';

// Constellation
import { Anchor, Box, Divider, Icon, Image, Text, TextButton } from '@zillow/constellation-10';
import { IconPhotosFilled } from '@zillow/constellation-icons';

// Components
import LazyLoad from '../LazyLoad';

// Misc / utils
import { parser as comparablesParser } from '../utils/comparablesUtils';
import { pluralize } from '../utils/stringUtils';
import { useAppSelector } from '../flux/configureStore';
import {
  cardAddressStyles,
  cardContainerRecipe,
  cardThumbContainerStyles,
  cardThumbnailStyles,
  statusDotRecipe,
} from './styles';

// Types
import type React from 'react';
import type ComparablesModelType from '../models/Comparables';

type ZestimateType = { rentZestimate: number };
type CompsCarouselCardPropTypes = {
  listing: ComparablesModelType & Partial<ZestimateType>;
  isHovered?: boolean;
  lockHover?: boolean;
  isStriped: boolean;
};

const MAX_CHARS_PER_LINE = 21;

const CompsCarouselCardComponent = ({ listing, isHovered, lockHover, isStriped }: CompsCarouselCardPropTypes) => {
  const longestAddress = useAppSelector((state) => comparablesParser.getLongestAddress(state.comparables.items));

  const propertyPhoto =
    listing && listing.propertyPhotos && listing.propertyPhotos[0] && listing.propertyPhotos[0].medium;
  const listingPriceSqft = isNil(listing.pricePerSqft) ? '—' : `${listing.pricePerSqft} per sq. ft.`;

  const StandardHeader = (
    <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box css={cardThumbContainerStyles}>
        {propertyPhoto ? (
          <>
            {listing.isActive ? (
              <Anchor href={listing.url} target="_blank" rel="noopener noreferrer">
                <LazyLoad>
                  <Image src={propertyPhoto} alt={`Photo of ${listing.street}`} css={cardThumbnailStyles} />
                </LazyLoad>
              </Anchor>
            ) : (
              <LazyLoad>
                <Image src={propertyPhoto} alt={`Photo of ${listing.street}`} css={cardThumbnailStyles} />
              </LazyLoad>
            )}
          </>
        ) : (
          <Icon
            render={<IconPhotosFilled />}
            size="xl"
            css={{ color: 'icon.subtle' }}
            aria-label={`Photo of ${listing.street} is not available`}
          />
        )}
      </Box>
      <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
        <Text css={{ marginTop: '200', marginLeft: '200' }}>
          <strong>{listing.monthlyRent}/mo</strong>
        </Text>
        <Text css={{ marginBottom: '200', marginLeft: '200' }}>{listingPriceSqft}</Text>
      </Box>
    </Box>
  );

  const isActive = listing.isActive;
  const listingBeds = isNil(listing.beds) ? '—' : comparablesParser.beds(listing.beds);
  const listingBaths = isNil(listing.baths) ? '—' : pluralize('bath', listing.baths, true);
  const listingSqft = isNil(listing.sqft) ? '—' : `${listing.sqft} sq. ft.`;
  const listingLaundryAmenity = isNil(listing.laundry) ? '—' : listing.laundry;
  const listingParkingAmenity = isNil(listing.parking) ? '—' : listing.parking;
  const listingAcAmenity = isNil(listing.ac) ? '—' : listing.ac;
  const listingHeatingAmenity = isNil(listing.heating) ? '—' : listing.heating;
  const listingPetsAmenity = isNil(listing.pets) ? '—' : listing.pets;

  return (
    <Box css={cardContainerRecipe.raw({ isHovered: !!(isHovered || lockHover), isStriped })}>
      {StandardHeader}
      <Divider css={{ width: 'calc(100% + {sizes.400})', marginBlock: '200' }} />

      <Box
        css={{
          ...cardAddressStyles,
          ...(longestAddress.length > MAX_CHARS_PER_LINE ? { height: '60px' } : {}),
        }}
      >
        {isActive ? (
          <TextButton textStyle="body-sm-bold" css={{ marginLeft: '200' }} asChild>
            <a href={listing.url} target="_blank" rel="noopener noreferrer">
              {listing.street}
            </a>
          </TextButton>
        ) : (
          <Text textStyle="body-sm" css={{ marginLeft: '200' }}>
            {listing.street}
          </Text>
        )}
      </Box>
      <Text textStyle="body-sm" css={{ marginBottom: '200', marginLeft: '200' }}>
        {listing.distance === '1.0' ? `${listing.distance} mile away` : `${listing.distance} miles away`}
      </Text>
      <Text textStyle="body-sm" css={{ marginBottom: '200', marginLeft: '200' }}>
        <Box css={statusDotRecipe.raw({ isActive: !!isActive })} />
        {listing.status}
      </Text>
      <Divider css={{ width: 'calc(100% + {sizes.400})', marginBlock: '200' }} />
      <Text textStyle="body-sm" css={{ marginBottom: '200', marginLeft: '200' }}>
        {listingBeds}
      </Text>
      <Text textStyle="body-sm" css={{ marginBottom: '200', marginLeft: '200' }}>
        {listingBaths}
      </Text>
      <Text textStyle="body-sm" css={{ marginBottom: '200', marginLeft: '200' }}>
        {listingSqft}
      </Text>
      <Divider css={{ width: 'calc(100% + {sizes.400})', marginBlock: '200' }} />
      <Text textStyle="body-sm" css={{ marginBottom: '200', marginLeft: '200' }}>
        {listingLaundryAmenity}
      </Text>
      <Divider css={{ width: 'calc(100% + {sizes.400})', marginBlock: '200' }} />
      <Text textStyle="body-sm" css={{ marginBottom: '200', marginLeft: '200' }}>
        {listingParkingAmenity}
      </Text>
      <Divider css={{ width: 'calc(100% + {sizes.400})', marginBlock: '200' }} />
      <Text textStyle="body-sm" css={{ marginBottom: '200', marginLeft: '200' }}>
        {listingAcAmenity}
      </Text>
      <Divider css={{ width: 'calc(100% + {sizes.400})', marginBlock: '200' }} />
      <Text textStyle="body-sm" css={{ marginBottom: '200', marginLeft: '200' }}>
        {listingHeatingAmenity}
      </Text>
      <Divider css={{ width: 'calc(100% + {sizes.400})', marginBlock: '200' }} />
      <Text textStyle="body-sm" css={{ marginLeft: '200' }}>
        {listingPetsAmenity}
      </Text>
      <Divider css={{ width: 'calc(100% + {sizes.400})', marginTop: '200' }} />
    </Box>
  );
};

const CompsCarouselCard: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<CompsCarouselCardPropTypes>>
> = (props) => {
  if (!props.listing) {
    return null;
  }
  return <CompsCarouselCardComponent {...props} />;
};

export default CompsCarouselCard;
